<template>
  <div class="site-create-step1">
    <div class="cs1-scroll">
      <div class="cs1-top">
        <div class="cs1-title">{{ isEditMode ? "기본 정보 수정" : "기본 정보 입력" }}</div>
        <div class="cs1-subtitle">
          현장 등록을 위해 기본 정보를 입력해주세요.
        </div>
      </div>

      <div class="cs1-section">
        <div class="cs1-section-title">현장정보</div>

        <div class="cs1-grid">
          <div class="cs1-row">
            <label class="cs1-label">대리점</label>
            <div class="cs1-field">
              <select v-model="form.agencyId" class="cs1-select">
                <option :value="null">대리점을 선택하지 않음</option>
                <option
                  v-for="agency in agencyOptions"
                  :key="agency.id"
                  :value="agency.id"
                >
                  {{ agency.name }}
                </option>
              </select>
              <div v-if="isEditMode && !form.agencyId" class="cs1-helper">
                현재 연동된 대리점이 없습니다.
              </div>
            </div>
          </div>

          <div class="cs1-row">
            <label class="cs1-label">발주처</label>
            <div class="cs1-field">
              <select v-model="form.clientId" class="cs1-select">
                <option :value="null">발주처를 선택하지 않음</option>
                <option
                  v-for="client in clientOptions"
                  :key="client.id"
                  :value="client.id"
                >
                  {{ client.name }}
                </option>
              </select>
              <div v-if="isEditMode && !form.clientId" class="cs1-helper">
                현재 연동된 발주처가 없습니다.
              </div>
            </div>
          </div>

          <div class="cs1-row">
            <label class="cs1-label required">공사명</label>
            <div class="cs1-field">
              <input
                v-model="form.siteName"
                type="text"
                class="cs1-input"
                placeholder="공사명을 입력해주세요"
              />
            </div>
          </div>

          <div class="cs1-row">
            <label class="cs1-label">현장 영문명</label>
            <div class="cs1-field">
              <input
                v-model="form.siteEnglishName"
                type="text"
                class="cs1-input"
                placeholder="영문, 숫자, 공백, -, _ 입력 가능"
              />
            </div>
          </div>

          <div class="cs1-row">
            <label class="cs1-label required">주소</label>
            <div class="cs1-field">
              <div class="cs1-address-row">
                <input
                  v-model="form.address"
                  type="text"
                  class="cs1-input"
                  placeholder="주소 검색을 진행해주세요"
                  readonly
                />
                <button type="button" class="cs1-btn cs1-btn-search" @click="searchAddress">
                  주소 검색
                </button>
              </div>
              <div v-if="extraAddress" class="cs1-helper">
                참고주소: {{ extraAddress }}
              </div>
            </div>
          </div>

          <div class="cs1-row">
            <label class="cs1-label">상세주소</label>
            <div class="cs1-field">
              <input
                ref="addressDetailInput"
                v-model="form.addressDetail"
                type="text"
                class="cs1-input"
                placeholder="상세주소를 입력해주세요"
              />
            </div>
          </div>

          <div class="cs1-row">
            <label class="cs1-label">설치일</label>
            <div class="cs1-field">
              <DatePicker
                v-model="startDateModel"
                dateFormat="yy-mm-dd"
                showIcon
                inputClass="cs1-date-input"
                class="cs1-date-picker"
                placeholder="연도-월-일"
              />
            </div>
          </div>
        </div>
      </div>

      <div class="cs1-divider"></div>

      <div class="cs1-section">
        <div class="cs1-section-title">업체정보</div>

        <div class="cs1-grid">
          <div class="cs1-row">
            <label class="cs1-label">담당자</label>
            <div class="cs1-field">
              <input
                v-model="form.manager"
                type="text"
                class="cs1-input"
                placeholder="담당자명을 입력해주세요"
              />
            </div>
          </div>

          <div class="cs1-row">
            <label class="cs1-label">연락처</label>
            <div class="cs1-field">
              <input
                v-model="form.phone"
                type="text"
                class="cs1-input"
                @input="onlyNumber"
                placeholder="연락처를 입력해주세요"
              />
            </div>
          </div>

          <div class="cs1-row cs1-row-top">
            <label class="cs1-label">현장사진</label>
            <div class="cs1-field">
              <div class="cs1-upload-row">
                <label class="cs1-upload-btn">
                  현장사진 업로드
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    @change="handleSiteImagesChange"
                    hidden
                  />
                </label>
                <div class="cs1-upload-text">
                  {{ selectedFileText }}
                </div>
              </div>

              <div class="cs1-image-box">
                <template v-if="visibleSiteImages.length > 0">
                  <div class="cs1-image-list">
                    <div
                      v-for="(image, index) in visibleSiteImages"
                      :key="image.uuid"
                      class="cs1-image-item"
                    >
                      <div class="cs1-image-thumb-wrap">
                        <img
                          :src="image.previewUrl"
                          :alt="image.originalFileName || 'site-image'"
                          class="cs1-image-thumb"
                        />
                      </div>

                      <div class="cs1-image-info">
                        <div class="cs1-image-name">
                          {{ image.originalFileName || "이미지" }}
                        </div>
                        <div class="cs1-image-order">순서 {{ index + 1 }}</div>
                      </div>

                      <div class="cs1-image-actions">
                        <button
                          type="button"
                          class="cs1-icon-btn"
                          @click="moveSiteImage(index, -1)"
                          :disabled="index === 0"
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          class="cs1-icon-btn"
                          @click="moveSiteImage(index, 1)"
                          :disabled="index === visibleSiteImages.length - 1"
                        >
                          ↓
                        </button>
                        <button
                          type="button"
                          class="cs1-icon-btn danger"
                          @click="removeSiteImage(image.uuid)"
                        >
                          삭제
                        </button>
                      </div>
                    </div>
                  </div>
                </template>

                <template v-else>
                  <div class="cs1-empty-text">
                    등록된 현장사진이 없습니다.
                  </div>
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="cs1-bottom-actions">
        <button
          type="button"
          class="cs1-btn cs1-btn-primary"
          :disabled="saving || loadingOptions"
          @click="submitStep1"
        >
          {{ saving ? (isEditMode ? "수정 중..." : "등록 중...") : "다음" }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import api from "@/api/axios"

export default {
  name: "CreateSiteStep1",

  props: {
    mode: {
      type: String,
      default: "create",
    },
    formValue: {
      type: Object,
      default: () => ({}),
    },
  },

  emits: ["next"],

  data() {
    return {
      form: {
        siteId: null,
        agencyId: null,
        agencyName: "",
        clientId: null,
        clientName: "",
        siteName: "",
        siteEnglishName: "",
        address: "",
        addressDetail: "",
        startDate: "",
        endDate: "",
        manager: "",
        phone: "",

        siteImages: [],
        deletedSiteImageUuids: [],
        siteImageUploadSessionId: null,
      },

      agencyOptions: [],
      clientOptions: [],

      loadingOptions: false,
      saving: false,
      isPostcodeScriptLoaded: false,
      zonecode: "",
      extraAddress: "",
      selectedFileText: "선택된 파일 없음",
    }
  },

  computed: {
    isEditMode() {
      return this.mode === "edit"
    },

    startDateModel: {
      get() {
        if (!this.form.startDate) return null
        return new Date(this.form.startDate)
      },
      set(value) {
        if (!value) {
          this.form.startDate = ""
          return
        }

        const yyyy = value.getFullYear()
        const mm = String(value.getMonth() + 1).padStart(2, "0")
        const dd = String(value.getDate()).padStart(2, "0")
        this.form.startDate = `${yyyy}-${mm}-${dd}`
      },
    },

    visibleSiteImages() {
      return (this.form.siteImages || []).filter((item) => !item.deleted)
    },
  },

  watch: {
    formValue: {
      immediate: true,
      deep: true,
      handler(value) {
        this.applyFormValue(value || {})
      },
    },
  },

  async mounted() {
    this.loadPostcodeScript()
    await this.fetchAgencyList()
    await this.fetchClientList()
  },

  methods: {
    onlyNumber(e) {
      this.form.phone = e.target.value.replace(/[^0-9]/g, '')
    },
    applyFormValue(value) {
      this.form = {
        siteId: value.siteId ?? null,
        agencyId: value.agencyId ?? null,
        agencyName: value.agencyName ?? "",
        clientId: value.clientId ?? null,
        clientName: value.clientName ?? "",
        siteName: value.siteName ?? "",
        siteEnglishName: value.siteEnglishName ?? "",
        address: value.address ?? "",
        addressDetail: value.addressDetail ?? "",
        startDate: value.startDate ?? "",
        endDate: value.endDate ?? "",
        manager: value.manager ?? "",
        phone: value.phone ?? "",
        siteImages: Array.isArray(value.siteImages) ? [...value.siteImages] : [],
        deletedSiteImageUuids: Array.isArray(value.deletedSiteImageUuids)
          ? [...value.deletedSiteImageUuids]
          : [],
        siteImageUploadSessionId: value.siteImageUploadSessionId ?? null,
      }
    },

    async fetchAgencyList() {
      try {
        this.loadingOptions = true

        const response = await api.get("/app/agency/list", {
          params: {
            page: 0,
            size: 999,
            deleted: false,
            orderBy: "nameAsc",
          },
        })

        const content = response.data?.result?.content ?? response.data?.result ?? []
        this.agencyOptions = content.map((item) => ({
          id: item.id,
          name: item.name,
        }))
      } catch (e) {
        console.error("agency list load error", e)
        this.agencyOptions = []
      } finally {
        this.loadingOptions = false
      }
    },

    async fetchClientList() {
      try {
        this.loadingOptions = true

        const response = await api.get("/app/client/list", {
          params: {
            page: 0,
            size: 999,
            deleted: false,
            orderBy: "nameAsc",
          },
        })

        const content = response.data?.result?.content ?? response.data?.result ?? []
        this.clientOptions = content.map((item) => ({
          id: item.id,
          name: item.name,
        }))
      } catch (e) {
        console.error("client list load error", e)
        this.clientOptions = []
      } finally {
        this.loadingOptions = false
      }
    },

    handleSiteImagesChange(event) {
      const files = Array.from(event.target.files || [])
      if (files.length === 0) return

      const appended = files.map((file, idx) => ({
        uuid: `new-${Date.now()}-${idx}-${Math.random().toString(36).slice(2, 10)}`,
        attachFileNo: null,
        previewUrl: URL.createObjectURL(file),
        originalFileName: file.name,
        sortOrder: this.visibleSiteImages.length + idx + 1,
        isNew: true,
        deleted: false,
        file,
      }))

      this.form.siteImages = [...(this.form.siteImages || []), ...appended]
      this.selectedFileText =
        files.length === 1 ? files[0].name : `${files.length}개 파일 선택`
      this.refreshSiteImageSortOrder()

      event.target.value = ""
    },

    removeSiteImage(uuid) {
      const target = (this.form.siteImages || []).find((item) => item.uuid === uuid)
      if (!target) return

      target.deleted = true

      if (target.previewUrl && target.isNew) {
        try {
          URL.revokeObjectURL(target.previewUrl)
        } catch (_) {
          // noop
        }
      }

      this.refreshSiteImageSortOrder()
    },

    moveSiteImage(index, direction) {
      const list = [...this.visibleSiteImages]
      const targetIndex = index + direction
      if (targetIndex < 0 || targetIndex >= list.length) return

      const temp = list[index]
      list[index] = list[targetIndex]
      list[targetIndex] = temp

      const hidden = (this.form.siteImages || []).filter((item) => item.deleted)
      this.form.siteImages = [...list, ...hidden]
      this.refreshSiteImageSortOrder()
    },

    refreshSiteImageSortOrder() {
      const visible = this.visibleSiteImages
      visible.forEach((item, index) => {
        item.sortOrder = index + 1
      })
    },

    validateStep1() {
      if (!this.form.siteName?.trim()) {
        alert("공사명을 입력해주세요.")
        return false
      }

      this.form.siteEnglishName = this.form.siteEnglishName?.trim() || ""

      if (this.form.siteEnglishName) {
        if (!/^[a-z0-9_ -]+$/i.test(this.form.siteEnglishName)) {
          alert("현장 영문명은 영문, 숫자, 공백, -, _ 만 입력 가능합니다.")
          return false
        }
      }

      if (!this.form.address?.trim()) {
        alert("주소를 입력해주세요.")
        return false
      }

      return true
    },

    buildCreateFormData() {
      const formData = new FormData()

      const paramsObj = {
        agencyId: this.form.agencyId,
        clientId: this.form.clientId,
        siteName: this.form.siteName,
        siteEnglishName: this.form.siteEnglishName,
        address: this.form.address,
        addressDetail: this.form.addressDetail,
        startDate: this.form.startDate,
        manager: this.form.manager,
        phone: this.form.phone,
      }

      formData.append(
        "paramsObj",
        new Blob([JSON.stringify(paramsObj)], { type: "application/json" })
      )

      this.visibleSiteImages
        .filter((item) => item.isNew && item.file)
        .forEach((item) => {
          formData.append("siteMainImageFileList", item.file)
        })

      return formData
    },

    buildUpdateFormData() {
      const formData = new FormData()

      const remainSiteMainImages = this.visibleSiteImages
        .filter((item) => !item.isNew && item.attachFileNo)
        .map((item, index) => ({
          attachFileNo: item.attachFileNo,
          sortOrder: index + 1,
        }))

      const newSiteMainImages = this.visibleSiteImages
        .filter((item) => item.isNew)
        .map((item, index) => ({
          tempId: item.uuid,
          sortOrder: index + 1,
        }))

      const paramsObj = {
        id: this.form.siteId,
        agencyId: this.form.agencyId,
        clientId: this.form.clientId,

        // 추가
        agencyClearYn: this.form.agencyId === null,
        clientClearYn: this.form.clientId === null,

        siteName: this.form.siteName,
        siteEnglishName: this.form.siteEnglishName,
        address: this.form.address,
        addressDetail: this.form.addressDetail,
        startDate: this.form.startDate,
        endDate: this.form.endDate,
        manager: this.form.manager,
        phone: this.form.phone,
        siteMainImagesChanged: true,
        remainSiteMainImages,
        newSiteMainImages,
      }

      formData.append("paramsObj", JSON.stringify(paramsObj))

      this.visibleSiteImages
        .filter((item) => item.isNew && item.file)
        .forEach((item) => {
          formData.append("siteMainImageFileList", item.file)
          formData.append("siteImageTempIds", item.uuid)
        })

      return formData
    },

    async submitStep1() {
      if (!this.validateStep1()) return

      try {
        this.saving = true

        let res

        if (this.isEditMode && this.form.siteId) {
          const formData = this.buildUpdateFormData()
          res = await api.post("/app/site/update", formData)
        } else {
          const formData = this.buildCreateFormData()
          res = await api.post("/app/site/create", formData, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          })
        }

        const result = res.data?.result
        const siteId = result?.id ?? this.form.siteId

        if (!siteId) {
          alert(
            this.isEditMode
              ? "현장 수정은 되었지만 siteId를 확인하지 못했습니다."
              : "현장 등록은 되었지만 siteId를 받지 못했습니다."
          )
          return
        }

        this.$emit("next", {
          ...this.form,
          siteId,
          agencyId: result?.agencyId ?? this.form.agencyId,
          agencyName: result?.agencyName ?? this.findAgencyName(this.form.agencyId),
          clientId: result?.clientId ?? this.form.clientId,
          clientName: result?.clientName ?? this.findClientName(this.form.clientId),
          siteImages: this.visibleSiteImages.map((item, index) => ({
            ...item,
            sortOrder: index + 1,
          })),
        })
      } catch (e) {
        console.error(e)
        alert(this.isEditMode ? "현장 수정 실패" : "현장 등록 실패")
      } finally {
        this.saving = false
      }
    },

    findAgencyName(id) {
      if (!id) return ""
      return this.agencyOptions.find((item) => item.id === id)?.name || ""
    },

    findClientName(id) {
      if (!id) return ""
      return this.clientOptions.find((item) => item.id === id)?.name || ""
    },

    loadPostcodeScript() {
      if (window.kakao && window.kakao.Postcode) {
        this.isPostcodeScriptLoaded = true
        return
      }

      const existed = document.querySelector(
        'script[src="//t1.kakaocdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js"]'
      )

      if (existed) {
        existed.addEventListener("load", () => {
          this.isPostcodeScriptLoaded = true
        })
        return
      }

      const script = document.createElement("script")
      script.src = "//t1.kakaocdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js"
      script.async = true
      script.onload = () => {
        this.isPostcodeScriptLoaded = true
      }
      script.onerror = () => {
        console.error("postcode script load failed")
      }

      document.head.appendChild(script)
    },

    searchAddress() {
      if (!(window.kakao && window.kakao.Postcode)) {
        alert("주소 검색 스크립트가 아직 로드되지 않았습니다.")
        return
      }

      new window.kakao.Postcode({
        oncomplete: (data) => {
          let addr = ""
          let extraAddr = ""

          if (data.userSelectedType === "R") {
            addr = data.roadAddress
          } else {
            addr = data.jibunAddress
          }

          if (data.userSelectedType === "R") {
            if (data.bname && /[동|로|가]$/g.test(data.bname)) {
              extraAddr += data.bname
            }

            if (data.buildingName && data.apartment === "Y") {
              extraAddr += extraAddr ? `, ${data.buildingName}` : data.buildingName
            }

            if (extraAddr) {
              extraAddr = `(${extraAddr})`
            }
          }

          this.zonecode = data.zonecode || ""
          this.form.address = addr
          this.extraAddress = extraAddr

          this.$nextTick(() => {
            this.$refs.addressDetailInput?.focus()
          })
        },
      }).open()
    },
  },
}
</script>

<style scoped>
.site-create-step1 {
  --bg: #f8fafc;
  --card: #ffffff;
  --text: #111827;
  --muted: #6b7280;
  --line: #e5e7eb;

  height: 100%;
  min-height: 0;
  background: var(--bg);
}

.cs1-scroll {
  height: 100%;
  overflow-y: auto;
  padding: 34px 40px 28px;
  box-sizing: border-box;
}

.cs1-top {
  text-align: center;
  margin-bottom: 26px;
}

.cs1-title {
  font-size: 22px;
  font-weight: 800;
  color: #0f172a;
}

.cs1-subtitle {
  margin-top: 8px;
  font-size: 15px;
  color: var(--muted);
}

.cs1-section {
  background: transparent;
}

.cs1-section-title {
  text-align: center;
  font-size: 18px;
  font-weight: 800;
  color: #0f172a;
  margin-bottom: 28px;
}

.cs1-grid {
  max-width: 920px;
  margin: 0 auto;
}

.cs1-row {
  display: grid;
  grid-template-columns: 160px minmax(0, 1fr);
  gap: 16px;
  align-items: center;
  margin-bottom: 18px;
}

.cs1-row-top {
  align-items: start;
}

.cs1-label {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
}

.cs1-label.required::after {
  content: " *";
  color: #ef4444;
}

.cs1-field {
  min-width: 0;
}

.cs1-input,
.cs1-select {
  width: 100%;
  height: 50px;
  border-radius: 12px;
  border: 1px solid #cbd5e1;
  background: #ffffff;
  padding: 0 16px;
  font-size: 16px;
  color: #111827;
  box-sizing: border-box;
  outline: none;
}

.cs1-input:focus,
.cs1-select:focus {
  border-color: var(--color-primary);
  box-shadow: 0 0 0 3px rgba(251, 146, 60, 0.12);
}

.cs1-select {
  appearance: none;
  background-image:
    linear-gradient(45deg, transparent 50%, #6b7280 50%),
    linear-gradient(135deg, #6b7280 50%, transparent 50%);
  background-position:
    calc(100% - 22px) calc(50% - 3px),
    calc(100% - 16px) calc(50% - 3px);
  background-size: 6px 6px, 6px 6px;
  background-repeat: no-repeat;
  padding-right: 44px;
}

.cs1-helper {
  margin-top: 8px;
  font-size: 13px;
  color: #64748b;
}

.cs1-address-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 120px;
  gap: 10px;
}

.cs1-btn {
  height: 46px;
  padding: 0 16px;
  border-radius: 12px;
  border: 1px solid #d1d5db;
  background: #ffffff;
  cursor: pointer;
  font-weight: 700;
  font-size: 15px;
}

.cs1-btn-search {
  color: #111827;
}

.cs1-btn-primary {
  min-width: 120px;
  background: var(--color-primary);
  border-color: var(--color-primary);
  color: #ffffff;
}

.cs1-btn-primary:hover {
  background: var(--color-primary-border);
  border-color: var(--color-primary-border);
}

.cs1-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.cs1-divider {
  max-width: 1100px;
  margin: 26px auto 30px;
  border-top: 1px solid var(--line);
}

.cs1-upload-row {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 14px;
}

.cs1-upload-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 44px;
  padding: 0 18px;
  border-radius: 12px;
  border: 1px solid #f59e0b;
  background: #fffbeb;
  color: #d97706;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
}

.cs1-upload-text {
  font-size: 14px;
  color: #6b7280;
}

.cs1-image-box {
  border: 1px dashed #d1d5db;
  border-radius: 14px;
  background: #ffffff;
  padding: 14px;
}

.cs1-image-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.cs1-image-item {
  display: grid;
  grid-template-columns: 90px minmax(0, 1fr) auto;
  gap: 14px;
  align-items: center;
  padding: 10px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
}

.cs1-image-thumb-wrap {
  width: 90px;
  height: 72px;
  border-radius: 10px;
  overflow: hidden;
  background: #f3f4f6;
}

.cs1-image-thumb {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.cs1-image-info {
  min-width: 0;
}

.cs1-image-name {
  font-size: 14px;
  font-weight: 700;
  color: #111827;
  word-break: break-all;
}

.cs1-image-order {
  margin-top: 6px;
  font-size: 13px;
  color: #6b7280;
}

.cs1-image-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.cs1-icon-btn {
  height: 34px;
  padding: 0 12px;
  border: 1px solid #d1d5db;
  background: #fff;
  border-radius: 10px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 700;
}

.cs1-icon-btn.danger {
  color: #dc2626;
  border-color: #fecaca;
  background: #fff5f5;
}

.cs1-empty-text {
  text-align: center;
  color: #94a3b8;
  font-size: 14px;
  padding: 16px 0;
}

.cs1-bottom-actions {
  margin-top: 28px;
  display: flex;
  justify-content: flex-end;
}

.cs1-date-picker {
  width: 100%;
}

:deep(.cs1-date-input) {
  width: 100%;
  height: 50px;
  border-radius: 12px;
  border: 1px solid #cbd5e1;
  background: #ffffff;
  padding: 0 16px;
  font-size: 16px;
  color: #111827;
  box-sizing: border-box;
}

@media (max-width: 900px) {
  .cs1-scroll {
    padding: 20px 16px 24px;
  }

  .cs1-row {
    grid-template-columns: 1fr;
    gap: 8px;
  }

  .cs1-address-row {
    grid-template-columns: 1fr;
  }

  .cs1-image-item {
    grid-template-columns: 1fr;
  }
}
</style>